import { useState, type ReactElement } from "react";
import { ChartNoAxesCombined, RefreshCw } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "@playbookai/api-clients";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  SegmentedControl,
  Skeleton,
} from "@playbookai/ui";
import { useAuthStore } from "../../auth/auth-store.js";
import {
  adoptionQueryKey,
  getAdoptionSummary,
  type AdoptionPeriodDays,
} from "../../api/admin-adoption-api.js";
import {
  getTenantConfiguration,
  tenantConfigurationQueryKey,
} from "../../api/tenant-config-api.js";
import { useDateTimeFormatter } from "../../settings/format-date-time.js";
import { AdminTimeZoneLabel } from "./AdminTimeZoneLabel.js";
import { OnboardingToursAdmin } from "./OnboardingToursAdmin.js";
import { AdminSection } from "./AdminSection.js";
import { MetricStrip } from "./health/MetricStrip.js";
import { AdoptionChart } from "./health/AdoptionChart.js";
import { AdoptionLearnMore } from "./health/AdoptionLearnMore.js";
import {
  formatCount,
  moduleAvailability,
  type AdoptionModuleView,
} from "./health/adoption-view.js";

const PERIODS: readonly AdoptionPeriodDays[] = [7, 30, 90];
type PeriodValue = `${AdoptionPeriodDays}`;
const PERIOD_OPTIONS = PERIODS.map((period) => ({
  value: `${period}` as PeriodValue,
  label: `${period} days`,
}));

export function HealthAdmin(): ReactElement {
  const api = useApiClient();
  const dateTimeFormatter = useDateTimeFormatter();
  const tenantId = useAuthStore((state) => state.claims?.tenantId);
  const [days, setDays] = useState<AdoptionPeriodDays>(30);
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const adoption = useQuery({
    queryKey: adoptionQueryKey(tenantId ?? undefined, days),
    queryFn: ({ signal }) => getAdoptionSummary(api, days, signal),
  });
  const configuration = useQuery({
    queryKey: tenantConfigurationQueryKey(tenantId),
    queryFn: getTenantConfiguration,
  });
  const readings: readonly AdoptionModuleView[] = (
    adoption.data?.modules ?? []
  ).map((reading) => ({
    ...reading,
    availability: moduleAvailability(reading, configuration.data?.modules),
  }));
  const formatDay = (day: string): string => {
    const start = dateTimeFormatter.startOfDay(day);
    return start === null ? day : dateTimeFormatter.date(start);
  };

  return (
    <AdminSection
      icon={ChartNoAxesCombined}
      title="Adoption"
      description="See where teams are creating and completing work across this workspace."
      action={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="secondary" onClick={() => setLearnMoreOpen(true)}>
            Learn more
          </Button>
          <SegmentedControl<PeriodValue>
            options={PERIOD_OPTIONS}
            value={`${days}` as PeriodValue}
            onChange={(next) => setDays(Number(next) as AdoptionPeriodDays)}
            aria-label="Reporting period"
          />
        </div>
      }
      meta={null}
      data-testid="adoption-admin"
    >
      {adoption.error ? (
        <Card>
          <CardBody>
            <EmptyState
              variant="error"
              title="Couldn't load adoption data"
              description="The workspace activity reading is temporarily unavailable."
              action={
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => void adoption.refetch()}
                >
                  <RefreshCw className="size-4" aria-hidden />
                  Try again
                </Button>
              }
            />
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-5">
          <MetricStrip readings={readings} loading={adoption.isPending} />
          <Card>
            <CardHeader className="items-start">
              <div>
                <h2 className="text-base font-semibold text-pb-text">
                  Daily activity
                </h2>
                <p className="mt-1 text-xs text-pb-text-muted">
                  Creation and completion events by day, compared with the
                  previous {days}-day period.
                </p>
              </div>
              {adoption.data ? (
                <div className="text-right">
                  <p className="pb-tabular text-xl font-semibold text-pb-text">
                    {/* locale-number: a total event count, not an instant. */}
                    {formatCount(adoption.data.totalActivity)}
                  </p>
                  <p className="text-xs text-pb-text-muted">
                    {adoption.data.totalActivity === null
                      ? "partial data"
                      : "total events"}
                  </p>
                </div>
              ) : null}
            </CardHeader>
            <CardBody>
              {adoption.isPending ? (
                <Skeleton className="h-72 w-full" />
              ) : (
                <AdoptionChart
                  readings={readings}
                  days={days}
                  formatDay={formatDay}
                />
              )}
            </CardBody>
          </Card>
          <Card>
            <CardHeader>
              <div>
                <h2 className="text-base font-semibold text-pb-text">
                  How this is counted
                </h2>
                <p className="mt-1 text-xs text-pb-text-muted">
                  Definitions stay stable so period comparisons remain
                  meaningful.
                </p>
              </div>
            </CardHeader>
            <CardBody className="grid gap-4 sm:grid-cols-2">
              <p className="text-sm leading-relaxed text-pb-text-secondary">
                <strong className="font-medium text-pb-text">
                  Created work
                </strong>
                <br />
                SOLVEs, VSMs and Hoshin plans are counted on the day they were
                created, even if they are archived later.
              </p>
              <p className="text-sm leading-relaxed text-pb-text-secondary">
                <strong className="font-medium text-pb-text">
                  Completed assessments
                </strong>
                <br />
                LAMA is counted when an assessment wave closes and its score
                snapshot is recorded.
              </p>
            </CardBody>
          </Card>
          {adoption.data ? (
            <p className="text-right text-xs text-pb-text-muted">
              Updated{" "}
              <AdminTimeZoneLabel
                label={dateTimeFormatter.dateTime(
                  adoption.data.window.generatedAt,
                )}
                formatter={dateTimeFormatter}
                variant="inline"
              />
            </p>
          ) : null}
        </div>
      )}

      <OnboardingToursAdmin key={tenantId} />

      <AdoptionLearnMore
        open={learnMoreOpen}
        onClose={() => setLearnMoreOpen(false)}
      />
    </AdminSection>
  );
}
